"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faUser,
  faEnvelope,
  faStore,
  faUserTag,
  faCreditCard,
  faCalendarAlt,
  faCog,
  faSpinner,
  faTools, // Optional icon for Admin Dashboard
} from "@fortawesome/free-solid-svg-icons"
import { api } from "../utils/auth"

const UserDashboard = () => {
  const [user, setUser] = useState(null)
  const [plan, setPlan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await api.get("/dashboard")

        if (!response.data) {
          throw new Error("Failed to fetch dashboard data")
        }

        setUser(response.data.user)
        setPlan(response.data.plan)
      } catch (err) {
        setError(err.message || "An error occurred while fetching your data")
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  return (
    <div className="user-dashboard">
      <style jsx="true">{`
        .user-dashboard {
          max-width: 800px;
          margin: 2rem auto;
          padding: 0 1rem;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
        }
        .dashboard-header {
          margin-bottom: 2rem;
          text-align: center;
        }
        .dashboard-title {
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 0.5rem;
        }
        .dashboard-subtitle {
          color: #6b7280;
          font-size: 1rem;
        }
        .dashboard-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .dashboard-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
        .dashboard-card {
          background-color: white;
          border-radius: 0.5rem;
          overflow: hidden;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .dashboard-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
        .card-header {
          background-color: #00a99d;
          color: white;
          padding: 1rem 1.5rem;
          font-weight: 600;
          font-size: 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .card-content {
          padding: 1.5rem;
        }
        .info-row {
          display: flex;
          margin-bottom: 1rem;
          align-items: center;
        }
        .info-row:last-child {
          margin-bottom: 0;
        }
        .info-label {
          font-weight: 600;
          color: #4b5563;
          width: 120px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .info-value {
          color: #111827;
          flex: 1;
        }
        .action-button {
          background-color: #f33a3a;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 0.375rem;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease;
          display: inline-block;
          text-decoration: none;
          font-size: 1rem;
          text-align: center;
          margin-top: 1rem;
        }
        .action-button:hover {
          background-color: #e02e2e;
        }
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem 0;
          color: #6b7280;
        }
        .loading-spinner {
          animation: spin 1s linear infinite;
          font-size: 2rem;
          color: #00a99d;
          margin-bottom: 1rem;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .error-container {
          background-color: #fee2e2;
          border: 1px solid #fca5a5;
          color: #b91c1c;
          padding: 1rem;
          border-radius: 0.375rem;
          margin: 2rem 0;
          text-align: center;
        }
        .plan-badge {
          display: inline-block;
          background-color: #00a99d;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.875rem;
          font-weight: 600;
          margin-left: 0.5rem;
        }
        .plan-free {
          background-color: #6b7280;
        }
        .plan-premium {
          background-color: #f59e0b;
        }
        .plan-enterprise {
          background-color: #f33a3a;
        }
        .full-width {
          grid-column: 1 / -1;
        }
      `}</style>

      {loading ? (
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} className="loading-spinner" />
          <p>Loading your dashboard information...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>Error: {error}</p>
          <button className="action-button" onClick={() => window.location.reload()} style={{ marginTop: "1rem" }}>
            Try Again
          </button>
        </div>
      ) : !user || !plan ? (
        <div className="error-container">
          <p>Could not load user information.</p>
          <button className="action-button" onClick={() => window.location.reload()} style={{ marginTop: "1rem" }}>
            Try Again
          </button>
        </div>
      ) : (
        <>
          <div className="dashboard-header">
            <h1 className="dashboard-title">Welcome, {user.name}</h1>
            <p className="dashboard-subtitle">Manage your Shopify Scraper account and subscription</p>
          </div>

          <div className="dashboard-grid">
            <div className="dashboard-card">
              <div className="card-header">
                <FontAwesomeIcon icon={faUser} /> User Information
              </div>
              <div className="card-content">
                <div className="info-row">
                  <div className="info-label">
                    <FontAwesomeIcon icon={faUser} /> Name
                  </div>
                  <div className="info-value">{user.name}</div>
                </div>

                <div className="info-row">
                  <div className="info-label">
                    <FontAwesomeIcon icon={faEnvelope} /> Email
                  </div>
                  <div className="info-value">{user.email}</div>
                </div>

                {user.store_id && (
                  <div className="info-row">
                    <div className="info-label">
                      <FontAwesomeIcon icon={faStore} /> Store ID
                    </div>
                    <div className="info-value">{user.store_id}</div>
                  </div>
                )}

                <div className="info-row">
                  <div className="info-label">
                    <FontAwesomeIcon icon={faUserTag} /> Role
                  </div>
                  <div className="info-value">{user.role}</div>
                </div>
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-header">
                <FontAwesomeIcon icon={faCreditCard} /> Subscription Details
              </div>
              <div className="card-content">
                <div className="info-row">
                  <div className="info-label">
                    <FontAwesomeIcon icon={faCreditCard} /> Plan
                  </div>
                  <div className="info-value">
                    {plan.type || "N/A"}
                    <span
                      className={`plan-badge ${
                        plan.type === "Free"
                          ? "plan-free"
                          : plan.type === "Professional"
                            ? "professional"
                            : plan.type === "Bussiness"
                              ? "bussiness"
                              : ""
                      }`}
                    >
                      {plan.type || "N/A"}
                    </span>
                  </div>
                </div>

                <div className="info-row">
                  <div className="info-label">
                    <FontAwesomeIcon icon={faCalendarAlt} /> Start Date
                  </div>
                  <div className="info-value">
                    {plan.startDate ? new Date(plan.startDate).toLocaleDateString() : "N/A"}
                  </div>
                </div>

                <div className="info-row">
                  <div className="info-label">
                    <FontAwesomeIcon icon={faCalendarAlt} /> End Date
                  </div>
                  <div className="info-value">{plan.endDate ? new Date(plan.endDate).toLocaleDateString() : "N/A"}</div>
                </div>

                <Link to="/subscription" className="action-button">
                  Upgrade Plan
                </Link>
              </div>
            </div>

            <div className="dashboard-card full-width">
              <div className="card-header">
                <FontAwesomeIcon icon={faCog} /> Account Actions
              </div>
              <div className="card-content">
                <p style={{ marginBottom: "1rem", color: "#4b5563" }}>
                  Manage your subscription or update your profile settings.
                </p>

                <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                  <Link to="/settings" className="action-button">
                    Account Settings
                  </Link>

                  <Link to="/billing" className="action-button" style={{ backgroundColor: "#00a99d" }}>
                    Billing History
                  </Link>

                  <Link to="/support" className="action-button" style={{ backgroundColor: "#6b7280" }}>
                    Get Support
                  </Link>

                  {user.role === "admin" && (
                    <Link to="/admin" className="action-button" style={{ backgroundColor: "#111827" }}>
                      <FontAwesomeIcon icon={faTools} style={{ marginRight: "0.5rem" }} />
                      Admin Dashboard
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default UserDashboard
